/**
 *  Header
 *    Scripts used for the header section.
 *    Contains examples of how to initialise topbar elements as a carousel,
 *    adding sticky header on scroll and a search bar hide/show via CSS class.
 *    The onCartUpdate method to add cart count is required for all sites.
 *    Keep the functionality of this file to do with the overall header.
 * 
 *  Version
 *    1.0.0 - 2024/02/07
 */

import { CLASS_VISIBLE, CLASS_SCROLLING, CLASS_STICKY, CLASS_ACTIVE, CLASS_HIDDEN, CLASS_HAS_PROMOBAR, CLASS_SEARCH_OPEN } from "../../tools/constants/classes";
import { Section } from '../../tools/section';
import { Navigation, SELECTOR_NAVIGATION, SELECTOR_NAVIGATION_TOGGLE } from "./navigation";

const SELECTOR_CONTAINER = '[data-header]';
const SELECTOR_HEADER_INNER = '[data-header-inner]';
const SELECTOR_SEARCH_TOGGLE = '[data-header-search-toggle]';
const SELECTOR_SEARCH_BAR = '[data-search-bar]';
const SELECTOR_TOPBAR = '[data-topbar]';
const SELECTOR_MENU = '[data-menu-toggle]';
const SELECTOR_SEARCH_INPUT = '[data-search-input]';

class Header extends Section {
  private topbarHeight:number = 0;
  private lastPosition:number = 0;
  private topbarElement:HTMLElement | null = null;
  private navigation:Navigation | null = null;
  private navigationElement:HTMLElement | null = null;
  private navigationToggles:HTMLElement[] = [];
  private menuElement:HTMLElement| null = null;
  private headerInner:HTMLElement | null = null;
  private searchBar:HTMLElement | null = null;
  private searchToggle:HTMLElement | null = null;
  private searchInput: HTMLInputElement | null = null;

  queryChildren(container: HTMLElement) {
    this.topbarElement = document.querySelector(SELECTOR_TOPBAR);
    this.headerInner = container.querySelector(SELECTOR_HEADER_INNER);
    this.menuElement = container.querySelector(SELECTOR_MENU);
    this.navigationElement = document.querySelector(SELECTOR_NAVIGATION);
    this.searchBar = container.querySelector(SELECTOR_SEARCH_BAR);
    this.searchToggle = container.querySelector(SELECTOR_SEARCH_TOGGLE);
    this.navigationToggles = Array.from(
      container.querySelectorAll<HTMLElement>(SELECTOR_NAVIGATION_TOGGLE)
    );

    this.searchInput = container.querySelector(SELECTOR_SEARCH_INPUT);
  }

  attachEvents() {
    const onHomepage = document.body.classList.contains('c-page--index');
    const hasHero = document.querySelector('[data-page-hero]');
    this.checkHeaderPosition(onHomepage);

    if (this.navigationElement) {
      this.navigation = new Navigation(this.navigationElement);
      this.navigationToggles.forEach(toggle => {
        toggle.addEventListener("click", this.toggleNavigation.bind(this));
      });
    }

    if(this.searchToggle) {
      this.searchToggle.addEventListener('click', ()=> {
        this.searchToggle?.classList.toggle(CLASS_SEARCH_OPEN);
        this.searchBar?.classList.toggle(CLASS_SEARCH_OPEN);
        setTimeout(()=>{
          this.searchInput?.focus();
        }, 600);
      });
    }

    if(this.menuElement) {
      this.menuElement.addEventListener('click', () => {
        this.navigationElement?.classList.add(CLASS_ACTIVE);
      })
    }

    // Set header height on all pages except Homepage
    if (!onHomepage) {
      if(hasHero) return;
      this.setHeaderHeight();
      window.addEventListener("resize", () => {
        this.setHeaderHeight();
      });
    }
  }

  detatchEvents() {
    if(this.menuElement) {
      this.menuElement.removeEventListener('click', () => {
        this.navigationElement?.classList.add(CLASS_ACTIVE);
      })
    }

    if(this.searchToggle) {
      this.searchToggle.removeEventListener('click', ()=> {
        this.searchToggle?.classList.toggle(CLASS_SEARCH_OPEN);
        this.searchBar?.classList.toggle(CLASS_SEARCH_OPEN);
        setTimeout(()=>{
          this.searchInput?.focus();
        }, 600);
      });
    }

    if (this.navigationElement) {
      this.navigationToggles.forEach(toggle => {
        toggle.removeEventListener("click", this.toggleNavigation.bind(this));
      });
    }
  }

  checkHeaderPosition(onHomepage:any) {
    const top = document.documentElement.scrollTop;
    const topbarHeight = this.topbarElement ? this.topbarElement.clientHeight + 10 : 0;

    //Sticky header once you've scrolled past the top message bar
    if(top != this.lastPosition) {
      // Are we scrolling up or down?
      if (top < this.lastPosition || top === 0) { // Top
        this.container.classList.add(CLASS_VISIBLE);
        document.body.classList.add('is-header-visible');
        // if on the top position, show the promobar
        if (this.topbarElement && onHomepage && top === 0) {
          this.topbarElement.classList.remove(CLASS_HIDDEN);
        }
      } else if (top > this.lastPosition) { // Down
        this.container.classList.remove(CLASS_VISIBLE);
        document.body.classList.remove('is-header-visible');

        // on the homepage, hide the promobar when scrolling down
        if (this.topbarElement && onHomepage) {
          this.topbarElement.classList.add(CLASS_HIDDEN);
        }
      }

      if(top > topbarHeight) {
        this.container.classList.add(CLASS_STICKY);
      } else {
        this.container.classList.remove(CLASS_STICKY);
        this.container.classList.remove(CLASS_VISIBLE);
      }

      if(top >= 100) {
        this.container.classList.add(CLASS_SCROLLING);
      } else {
        this.container.classList.remove(CLASS_SCROLLING);
      }

      this.lastPosition = top;
    } else if (top == 0 && this.topbarElement) {
      // if on the top position, show the promobar
      document.body.classList.add(CLASS_HAS_PROMOBAR);
    }

    requestAnimationFrame(() => this.checkHeaderPosition(onHomepage));
  }
  
  setHeaderHeight() {
    const headerInnerHeight = this.headerInner?.clientHeight;
    const topbarHeight = this.topbarElement?.clientHeight;
    const headerHeight = (topbarHeight ? topbarHeight : 0) + (headerInnerHeight ? headerInnerHeight : 0);
    
    this.container.style.height = `${headerHeight}px`;
  }

  toggleNavigation(event: Event) {
    if (this.navigation) this.navigation.onToggleClicked(event);
  }
}

Section.register(SELECTOR_CONTAINER, Header);